import { FloatingMenu, useCurrentEditor } from '@tiptap/react';
import { HELPERS } from 'common';
import MODAL_TYPES from 'constants/modals';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useCreateFileManagerMetaMutation } from 'store/services/fileManagerMetaApi';
import { showModal } from 'store/slices/modal';
import Menu from '../Menu';
import { getBlockActions } from './Actions';
import InsertUrlEmbedModal from './InsertUrlEmbedModal';
import MenuTrigger from './MenuTrigger';
import insertContent from './helper/insertContent';
import onFileManagerUpload from './helper/onFileManagerUpload';
import shouldShow from './helper/shouldShow';

const BlockActionsMenu = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [youTubeModalOpen, setYouTubeModalOpen] = useState(false);
  const [vimeoModalOpen, setVimeoModalOpen] = useState(false);

  const dispatch = useDispatch();

  const { editor } = useCurrentEditor();
  const isEditorFocused = editor?.isFocused;

  useEffect(() => {
    editor?.chain().focus().run();
  }, [editor]);

  const onMenuOpen = () => {
    setMenuOpen(true);
  };

  const onMenuClose = () => {
    setMenuOpen(false);
  };

  const [fileManagerMeta] = useCreateFileManagerMetaMutation();

  const showFileManagerModal = useCallback(() => {
    dispatch(
      showModal({
        type: MODAL_TYPES.FILE_MANAGER,
        params: {
          modal: { title: 'File Manager', $top: 90 },
          body: {
            onUpload: (row) => onFileManagerUpload(editor, row, dispatch, fileManagerMeta),
          },
        },
      })
    );
  }, [dispatch, editor, fileManagerMeta]);

  const actions = useMemo(
    () =>
      getBlockActions({
        onMenuClose,
        showFileManagerModal,
        setYouTubeModalOpen,
        setVimeoModalOpen,
      }),
    [showFileManagerModal]
  );

  useEffect(() => {
    if (menuOpen) {
      editor?.on('selectionUpdate', onMenuClose);
      editor?.on('blur', onMenuClose);
      editor.commands.hidePlaceholder();
    }

    return () => {
      if (menuOpen) {
        editor?.off('selectionUpdate', onMenuClose);
        editor?.off('blur', onMenuClose);
        editor.commands.showPlaceholder();
      }
    };
  }, [editor, menuOpen]);

  return (
    <>
      <FloatingMenu tippyOptions={{ placement: 'left-end', offset: [10, 10] }} shouldShow={() => shouldShow(editor)}>
        <MenuTrigger
          menuOpen={menuOpen}
          onMenuClose={onMenuClose}
          onMenuOpen={onMenuOpen}
          isEditorFocused={isEditorFocused}
        />
      </FloatingMenu>
      <FloatingMenu tippyOptions={{ placement: 'right-end', offset: [12, 10] }}>
        {menuOpen && isEditorFocused && (
          <div className='p--2'>
            <Menu actions={actions} className='d-flex' />
          </div>
        )}
      </FloatingMenu>
      <InsertUrlEmbedModal
        visible={youTubeModalOpen}
        name='YouTube'
        onSuccess={(src) => {
          setYouTubeModalOpen(false);
          editor?.chain().focus().setYoutubeVideo({ src }).run();
        }}
        onClose={() => {
          setYouTubeModalOpen(false);
        }}
      />
      <InsertUrlEmbedModal
        visible={vimeoModalOpen}
        name='Vimeo'
        onSuccess={(src) => {
          setVimeoModalOpen(false);

          const vimeoUrl = HELPERS.getVimeoUrl(src);

          vimeoUrl &&
            insertContent(editor, {
              type: 'vimeo',
              attrs: { src: HELPERS.getVimeoUrl(src), contenteditable: false, 'data-ready': true },
            });
        }}
        onClose={() => {
          setVimeoModalOpen(false);
        }}
      />
    </>
  );
};

export default BlockActionsMenu;
